import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { CheckSquare, ExternalLink, FileDown, LayoutGrid, Printer, Refrigerator, Square, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button, Card, Field, Input, Select, Spinner } from "../../components/ui-kit";
import { formatItalianDate } from "../../lib/dateUtils";
import { setPrintPageSize, clearPrintPageSize } from "../../lib/printPageSize";
import { elementToPdf, pdfFileName, preparePdfWindow } from "../../lib/pdfExport";
import { QrLabel62x100Sheet, printQrLabels62x100, qrLabels62x100Pdf } from "../../components/QrLabel62x100";

// Quadretto A4 "QR code produzioni frigo/celle": un QR per ogni ricetta
// approvata (anche se non ancora prodotta nella sede, così si può stampare in
// anticipo), da apporre su frigoriferi e celle. È il QR FISSO di
// tracciabilità (fixed_qr.py): si stampa una volta e resta valido; alla
// scansione mostra l'ultima produzione, i processi di abbattimento e tutti i
// lotti ancora in conservazione, ciascuno con la propria scheda di
// tracciabilità. Diverso dal Libro ingredienti (solo ingredienti/allergeni).
const qrUrl = (row) => `${window.location.origin}/vetrina/${row.token}`;

const FORMATS = {
  a4: { label: "Quadretto A4", icon: LayoutGrid },
  label62: { label: "Etichetta singola 62×100", icon: Tag },
};

export default function QrProduzioniCelle() {
  const { ctx } = useOutletContext();
  const sites = ctx.sites || [];
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState([]);
  const [unitName, setUnitName] = useState("");
  const [format, setFormat] = useState("a4");
  const [pdfBusy, setPdfBusy] = useState(false);
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!siteId) { setRows([]); return; }
    setRows(null);
    api.get("/app/fridge-qr", { params: { site_id: siteId } })
      .then(({ data }) => { setRows(data.recipes || []); setSelected((data.recipes || []).map((r) => r.recipe_group_id)); })
      .catch(() => setRows([]));
  }, [siteId]);

  const chosen = useMemo(() => (rows || []).filter((r) => selected.includes(r.recipe_group_id)), [rows, selected]);
  const site = sites.find((s) => s.id === siteId);
  const toggle = (id) => setSelected((current) => current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  const allSelected = rows && rows.length > 0 && selected.length === rows.length;
  const print = () => {
    if (!chosen.length) return;
    if (format === "label62") { printQrLabels62x100(); return; }
    document.body.setAttribute("data-print", "fridge-a4");
    setPrintPageSize(210, 297);
    setTimeout(() => { window.print(); document.body.removeAttribute("data-print"); clearPrintPageSize(); }, 60);
  };
  // PDF A4 vero, da aprire e stampare: dal telefono è più affidabile della
  // stampa diretta della pagina (vedi lib/pdfExport.js).
  const createPdf = async () => {
    if (!chosen.length || !sheetRef.current) return;
    const win = preparePdfWindow();
    setPdfBusy(true);
    try {
      if (format === "label62") await qrLabels62x100Pdf(sheetRef.current, win, "produzioni", site?.name);
      else await elementToPdf(sheetRef.current.firstElementChild, { fileName: pdfFileName("qr-produzioni", site?.name, unitName), win });
    } catch {
      toast.error("Impossibile creare il PDF. Riprova.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="fridge-qr-page">
      <div className="flex flex-wrap items-end justify-between gap-4 no-print">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Documenti</p>
          <h1 className="font-heading text-3xl font-bold">QR code produzioni frigo/celle</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-500">Quadretto A4 da apporre su frigoriferi e celle. Ogni QR corrisponde a un prodotto: chi lo inquadra (anche l’ispettore ASL/NAS, senza login) trova l’ultima produzione, i processi di abbattimento e <strong>tutti i lotti ancora in conservazione</strong>, ognuno con la propria tracciabilità. Si stampa una volta: i QR restano validi e si aggiornano da soli a ogni nuova produzione.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="success" onClick={createPdf} loading={pdfBusy} disabled={!chosen.length} data-testid="fridge-qr-pdf">{!pdfBusy && <FileDown className="h-4 w-4" />} {pdfBusy ? "Creo il PDF…" : "Crea PDF e stampa"}</Button>
          <Button variant="outline" onClick={print} disabled={!chosen.length} data-testid="fridge-qr-print"><Printer className="h-4 w-4" /> Stampa diretta</Button>
        </div>
      </div>

      <Card className="grid gap-4 p-5 no-print md:grid-cols-2">
        <Field label="Sede"><Select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="fridge-qr-site">{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></Field>
        <Field label="Frigo / cella (facoltativo)" hint="Compare nell’intestazione del quadretto, es. CELLA NEGATIVA 1 o FRIGO LABORATORIO."><Input value={unitName} onChange={(e) => setUnitName(e.target.value.toUpperCase())} placeholder="ES. CELLA NEGATIVA 1" data-testid="fridge-qr-unit" /></Field>
        <div className="md:col-span-2">
          <Field label="Formato di stampa">
            <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
              {Object.entries(FORMATS).map(([key, f]) => (
                <button key={key} type="button" role="radio" aria-checked={format === key} onClick={() => setFormat(key)}
                  className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs font-semibold transition-colors ${format === key ? "border-navy bg-navy text-white" : "border-hairline text-navy hover:bg-slate-50"}`} data-testid={`fridge-qr-format-${key}`}>
                  <f.icon className="h-4 w-4 shrink-0" /> {f.label}
                </button>
              ))}
            </div>
          </Field>
        </div>
      </Card>

      {rows === null ? <Spinner /> : rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 no-print">Nessuna ricetta approvata: il quadretto si crea dalle ricette approvate dal consulente.</Card>
      ) : <>
        <Card className="p-5 no-print">
          <button type="button" onClick={() => setSelected(allSelected ? [] : rows.map((r) => r.recipe_group_id))} className="mb-3 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-navy" data-testid="fridge-qr-toggle-all">
            {allSelected ? <CheckSquare className="h-5 w-5 text-emerald-600" /> : <Square className="h-5 w-5" />} {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
          </button>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((r) => (
              <label key={r.recipe_group_id} className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm ${selected.includes(r.recipe_group_id) ? "border-emerald-300 bg-emerald-50" : "border-hairline"}`}>
                <input type="checkbox" checked={selected.includes(r.recipe_group_id)} onChange={() => toggle(r.recipe_group_id)} className="mt-0.5 h-5 w-5 accent-emerald-600" />
                <span className="min-w-0">
                  <span className="block font-medium text-navy">{r.name}</span>
                  {r.productions > 0
                    ? <span className="block text-xs text-slate-500">Ultimo lotto {r.last_lot} · {formatItalianDate(r.last_date)} · {r.in_storage} {r.in_storage === 1 ? "lotto" : "lotti"} in conservazione</span>
                    : <span className="block text-xs text-amber-700">Nessuna produzione ancora: il QR è già valido e si aggiornerà alla prima produzione</span>}
                  <a href={qrUrl(r)} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex min-h-[32px] items-center gap-1 text-xs font-medium text-sky-800 underline">Verifica la scheda del QR <ExternalLink className="h-3 w-3" /></a>
                </span>
              </label>
            ))}
          </div>
        </Card>

        {format === "label62" ? (
          chosen.length > 0 && <QrLabel62x100Sheet sheetRef={sheetRef} labels={chosen.map((r) => ({
            key: r.recipe_group_id,
            url: qrUrl(r),
            kicker: "Produzioni · tracciabilità",
            title: r.name,
            scanText: "INQUADRA PER PRODUZIONI, LOTTI IN CONSERVAZIONE E ABBATTIMENTI",
            footer: `${ctx.tenant?.name?.toUpperCase() || ""}${unitName ? ` — ${unitName}` : site?.name ? ` — ${site.name.toUpperCase()}` : ""}`,
          }))} />
        ) : <>
          <div className="no-print overflow-x-auto" ref={sheetRef}>
            <FridgeQrSheet rows={chosen} company={ctx.tenant?.name} siteName={site?.name} unitName={unitName} />
          </div>
          {/* Copia per la stampa portalata in document.body (stesso motivo di
              Etichette.jsx): evita le pagine bianche "fantasma" in Chromium. */}
          {createPortal(
            <div className="print-area fridge-qr-print"><FridgeQrSheet rows={chosen} company={ctx.tenant?.name} siteName={site?.name} unitName={unitName} /></div>,
            document.body
          )}
        </>}
      </>}
    </div>
  );
}

function FridgeQrSheet({ rows, company, siteName, unitName }) {
  return (
    <section className="ingredient-a4-sheet bg-white text-navy" data-testid="fridge-qr-sheet">
      <header className="mb-5 border-b-2 border-navy pb-3 text-center">
        <div className="flex items-center justify-center gap-2"><Refrigerator className="h-6 w-6" strokeWidth={1.5} /><h2 className="font-heading text-3xl font-bold">QR CODE PRODUZIONI</h2></div>
        <p className="mt-1 text-lg font-bold">{unitName || "FRIGO / CELLE"}</p>
        <p className="mt-1 text-sm font-semibold">{company?.toUpperCase()}{siteName ? ` — ${siteName.toUpperCase()}` : ""}</p>
        <p className="mt-2 text-xs">INQUADRA IL QR CODE DEL PRODOTTO PER CONSULTARE PRODUZIONI, LOTTI IN CONSERVAZIONE, PROCESSI DI ABBATTIMENTO E TRACCIABILITÀ</p>
      </header>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 print:grid-cols-3">
        {rows.map((r) => (
          <div key={r.recipe_group_id} className="break-inside-avoid rounded-lg border border-navy/30 p-3 text-center" style={{ breakInside: "avoid" }}>
            <QRCodeCanvas value={qrUrl(r)} size={112} fgColor="#0B1D3A" className="mx-auto aspect-square h-auto max-w-full" />
            <div className="mt-2 font-heading text-sm font-bold leading-tight">{r.name}</div>
            <div className="mt-1 text-[9px] font-semibold">PRODUZIONI E ABBATTIMENTI</div>
          </div>
        ))}
      </div>
      <footer className="mt-5 border-t border-navy/20 pt-2 text-center text-[9px]">QR fissi: restano validi e mostrano sempre i dati aggiornati delle produzioni. Informazioni pubbliche autorizzate, nessun dato personale.</footer>
    </section>
  );
}
